import { html, CSSResultGroup } from 'lit';
import { property } from 'lit/decorators.js';
import { ifDefined } from 'lit/directives/if-defined.js';
import { BlocksElement } from '../../BlocksElement';
import styles from '../icon.styles';
import type { IconColorType, IconSize } from './icon.model';

/**
 * Shipping component
 * @csspart base- The component's base wrapper
 */
export class BlocksIconShipping extends BlocksElement {static styles: CSSResultGroup = styles;

  /**
   * Icon color variant
   * <cat-text-passage size="sm">
   * <ul>
   * <li>No color defined renders an icon with the currentColor of its parent element</li>
   * <li>**content-strong** renders an icon with the cat-theme-color-content-strong color token</li>
   * <li>**knockout** renders an icon with the cat-theme-color-content-knockout color token</li>
   * <li>**knockout-brand** renders a renders an icon with the cat-theme-color-content-knockout-brand color token</li>
   * <li>**information** renders an icon with the cat-theme-color-content-utility-information color token</li>
   * <li>**success** renders an icon with the cat-theme-color-content-utility-success color token</li>
   * <li>**warning** renders a renders an icon with the cat-theme-color-content-utility-warning color token</li>
   * <li>**error** renders an icon with the cat-theme-color-content-utility-error color token</li>
   * </ul>
   * </cat-text-passage>
   */
  @property({ type: String })
  color: IconColorType;

  /**
   * Title
   * 1) Icons should always have associated text. Use this to supply `aria-label` to icon if it stands by itself without any visual/visually-hidden text
   */
  @property({ type: String })
  iconTitle: string | undefined;

  /**
   * Size property
   * - Default size is 16px
   * - **sm** renders a larger size than default (12px)
   * - **lg** renders a larger size than default (24px)
   * - **xl** renders a larger size than the lg variant (32px);
   */
  @property({ type: String })
  size: IconSize;

  connectedCallback() {
    super.connectedCallback();
    this.classList.add('cat-icon');
  }

  render() {
    const componentClassName = this.componentClassNames('cat-c-icon cat-c-icon-bi-directional', {
      'cat-c-icon--sm': this.size === 'sm',
      'cat-c-icon--lg': this.size === 'lg',
      'cat-c-icon--xl': this.size === 'xl',
      'cat-c-icon--content-strong': this.color === 'content-strong',
      'cat-c-icon--knockout': this.color === 'knockout',
      'cat-c-icon--knockout-brand': this.color === 'knockout-brand',
      'cat-c-icon--information': this.color === 'information',
      'cat-c-icon--success': this.color === 'success',
      'cat-c-icon--warning': this.color === 'warning',
      'cat-c-icon--error': this.color === 'error'
    });

    return html`
      <span part="base" class="${componentClassName}" role="presentation">
        <svg
          aria-hidden="${!this.iconTitle}"
          aria-label="${ifDefined(this.iconTitle)}"
          role="${this.iconTitle && 'img'}"
          width="24"
          height="24"
          viewBox="0 0 24 24"
          fill="none"
          xmlns="http://www.w3.org/2000/svg"
        >
          <path
            fill-rule="evenodd"
            clip-rule="evenodd"
            d="M3.11977 5.64014C2.64398 5.64014 2.25977 6.02435 2.25977 6.50014V17.0501H2.97236C3.45301 15.6468 4.78228 14.6401 6.35027 14.6401C7.91814 14.6401 9.24733 15.6466 9.72807 17.0498H13.6101C13.6268 17.0498 13.6433 17.0504 13.6598 17.0514V6.50014C13.6598 6.02435 13.2756 5.64014 12.7998 5.64014H3.11977ZM9.90439 18.5498H13.6101C13.6355 18.5498 13.6607 18.5485 13.6855 18.5461C13.8543 20.3623 15.379 21.7801 17.2399 21.7801C19.0995 21.7801 20.6232 20.3644 20.794 18.5501H22.4899C22.9041 18.5501 23.2399 18.2143 23.2399 17.8001V11.3501C23.2399 9.14439 21.4526 7.37012 19.2599 7.37012H15.1598V6.50014C15.1598 5.19592 14.104 4.14014 12.7998 4.14014H3.11977C1.81555 4.14014 0.759766 5.19592 0.759766 6.50014V17.8001C0.759766 18.2143 1.09555 18.5501 1.50977 18.5501H2.79619C2.96696 20.3644 4.49075 21.7801 6.35027 21.7801C8.20991 21.7801 9.73377 20.3642 9.90439 18.5498ZM21.7399 17.0501H20.6178C20.1372 15.6468 18.8079 14.6401 17.2399 14.6401C16.4633 14.6401 15.7452 14.8871 15.1598 15.3069V8.87012H19.2599C20.6272 8.87012 21.7399 9.97584 21.7399 11.3501V17.0501ZM17.2399 16.1401C16.0941 16.1401 15.1699 17.0643 15.1699 18.2101C15.1699 19.3559 16.0941 20.2801 17.2399 20.2801C18.3857 20.2801 19.3099 19.3559 19.3099 18.2101C19.3099 17.0643 18.3857 16.1401 17.2399 16.1401ZM4.28027 18.2101C4.28027 17.0643 5.20449 16.1401 6.35027 16.1401C7.49606 16.1401 8.42027 17.0644 8.42027 18.2101C8.42027 19.3559 7.49606 20.2801 6.35027 20.2801C5.20449 20.2801 4.28027 19.3559 4.28027 18.2101Z"
          />
        </svg>
      </span>
    `;
  }
}

if (customElements.get('cat-icon-shipping') === undefined) {
  customElements.define('cat-icon-shipping', BlocksIconShipping);
}

declare global {
  interface HTMLElementTagNameMap {
    'cat-icon-shipping': BlocksIconShipping;
  }
}
